import { Fragment, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    getSignInActivity,
    markSignInEventNotMe,
} from '../../services/Auth/SignInActivityService';
import { readErrorEnvelope } from '../../utils/errorEnvelope';
import { scrollToPageTop } from '../../utils/scrollToPageTop';
import RevokeOtherSessionsModal from './Modals/RevokeOtherSessionsModal';

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null;
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    );
};

const SIGN_IN_ACTIVITY_QUERY_KEY = 'sign-in-activity';

const EVENT_TYPE_LABELS = {
    'login-success':            'Signed in',
    'login-failed':             'Failed sign-in',
    'logout':                   'Signed out',
    'password-reset-used':      'Password reset',
    'pin-reset-used':           'Transaction PIN reset',
    'email-verified':           'Email verified',
    'sessions-revoked':         'Other sessions signed out',
    'not-me-reported':          'Reported as not you',
    'forced-logout-suspended':  'Signed out (account suspended)',
};

const EVENT_TYPE_BADGE_CLASS = {
    'login-success':            'badge-success',
    'login-failed':             'badge-danger',
    'logout':                   'badge-secondary',
    'password-reset-used':      'badge-warning',
    'pin-reset-used':           'badge-warning',
    'email-verified':           'badge-info',
    'sessions-revoked':         'badge-secondary',
    'not-me-reported':          'badge-dark',
    'forced-logout-suspended':  'badge-secondary',
};

const REPORTABLE_AS_NOT_ME_TYPES = new Set([
    'login-success',
    'login-failed',
    'password-reset-used',
    'pin-reset-used',
]);

const FAILURE_REASON_LABELS = {
    'invalid-password':   'Wrong password',
    'unknown-email':      'Unknown email',
    'account-suspended':  'Account suspended',
    'account-inactive':   'Account inactive',
    'partner-inactive':   'Vault inactive',
    'email-unverified':   'Email not verified',
    'throttled':          'Too many attempts',
};

const formatEventDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const formatLocation = (city, country) => {
    if (city && country) return `${city}, ${country}`;
    if (country) return country;
    if (city) return city;
    return '-';
};

const eventTypeBadge = (eventType) => {
    const cls = EVENT_TYPE_BADGE_CLASS[eventType] || 'badge-secondary';
    const label = EVENT_TYPE_LABELS[eventType] || eventType || 'Unknown';
    return <span className={`badge badge-rounded ${cls}`}>{label}</span>;
};

const SignInActivity = () => {
    const queryClient = useQueryClient();

    const [page, setPage] = useState(1);
    // Held so a future step can open the revoke-other-sessions modal seeded
    // with the event the user just reported, so the two actions chain in one flow.
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [pendingTriggerUuid, setPendingTriggerUuid] = useState(null);

    const activityQuery = useQuery({
        queryKey: [SIGN_IN_ACTIVITY_QUERY_KEY, page],
        queryFn:  () => getSignInActivity({ page }),
        placeholderData: keepPreviousData,
        staleTime: 0,
    });

    const events    = activityQuery.data?.data?.data ?? [];
    const totalPages = activityQuery.data?.data?.meta?.last_page ?? 1;

    const markNotMeMutation = useMutation({
        mutationFn: (eventUuid) => markSignInEventNotMe(eventUuid),
        onSuccess: (_response, eventUuid) => {
            queryClient.invalidateQueries({ queryKey: [SIGN_IN_ACTIVITY_QUERY_KEY] });
            toast.success('Marked as not you. Consider signing out other sessions.');
            setPendingTriggerUuid(eventUuid);
            setShowRevokeModal(true);
        },
        onError: (error) => {
            const { message } = readErrorEnvelope(error);
            toast.error(message || 'Could not mark that event. Please try again.');
        },
    });

    const openRevokeModal = () => {
        setPendingTriggerUuid(null);
        setShowRevokeModal(true);
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex flex-wrap gap-2 align-items-center justify-content-between">
                            <div>
                                <h4 className="card-title mb-1">Sign-in activity</h4>
                                <p className="text-muted small mb-0">
                                    Recent sign-ins on your account from the last 180 days.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={openRevokeModal}
                            >
                                Sign out other sessions
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {activityQuery.status === 'pending' ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : activityQuery.error ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load your sign-in activity. Please try again.
                                </p>
                            ) : events.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No sign-in activity recorded in the last 180 days.
                                </p>
                            ) : (() => {
                                const renderActionCell = (event) => {
                                    const reportable = REPORTABLE_AS_NOT_ME_TYPES.has(event.event_type);
                                    const alreadyReported = !!event.not_me_reported_at;
                                    const busy = markNotMeMutation.isPending
                                        && markNotMeMutation.variables === event.id;
                                    if (!reportable) return null;
                                    if (alreadyReported) return <span className="badge badge-rounded badge-dark">Reported</span>;
                                    return (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            disabled={busy}
                                            onClick={() => markNotMeMutation.mutate(event.id)}
                                        >
                                            {busy ? 'Reporting...' : "This wasn't me"}
                                        </button>
                                    );
                                };
                                const failureLabelFor = (event) => event.failure_reason
                                    ? (FAILURE_REASON_LABELS[event.failure_reason] || event.failure_reason)
                                    : null;
                                return (
                                    <>
                                        <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                            <table className="table verticle-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">When</th>
                                                        <th scope="col">Type</th>
                                                        <th scope="col">Device</th>
                                                        <th scope="col">IP</th>
                                                        <th scope="col">Location</th>
                                                        <th scope="col" className="text-end">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {events.map((event) => {
                                                        const failureLabel = failureLabelFor(event);
                                                        return (
                                                            <tr key={event.id}>
                                                                <td className="text-nowrap">{formatEventDate(event.created_at)}</td>
                                                                <td>
                                                                    <div className="d-flex flex-column align-items-start gap-1">
                                                                        {eventTypeBadge(event.event_type)}
                                                                        {failureLabel && (
                                                                            <span className="text-muted small">{failureLabel}</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                        <span>{event.device_label || 'Unknown device'}</span>
                                                                        {event.is_current_device && (
                                                                            <span className="badge badge-rounded badge-info">This device</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="text-nowrap">{event.ip_address || '-'}</td>
                                                                <td>{formatLocation(event.city, event.country)}</td>
                                                                <td className="text-end">
                                                                    {renderActionCell(event) ?? <span className="text-muted small">-</span>}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                            <table className="table verticle-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">When</th>
                                                        <th scope="col">Where</th>
                                                        <th scope="col" className="text-end">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {events.map((event) => {
                                                        const failureLabel = failureLabelFor(event);
                                                        return (
                                                            <tr key={event.id} className="align-top">
                                                                <td>
                                                                    <div className="fw-semibold text-nowrap">{formatEventDate(event.created_at)}</div>
                                                                    <div className="mt-1">{eventTypeBadge(event.event_type)}</div>
                                                                    {failureLabel && (
                                                                        <div className="text-muted small mt-1">{failureLabel}</div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="small mb-1">
                                                                        <i className="bi bi-laptop me-1" aria-hidden="true" />
                                                                        {event.device_label || 'Unknown device'}
                                                                        {event.is_current_device && (
                                                                            <span className="badge badge-rounded badge-info ms-2">This device</span>
                                                                        )}
                                                                    </div>
                                                                    {event.ip_address && (
                                                                        <div className="text-muted small mb-1">
                                                                            <i className="bi bi-hdd-network me-1" aria-hidden="true" />
                                                                            {event.ip_address}
                                                                        </div>
                                                                    )}
                                                                    {(event.city || event.country) && (
                                                                        <div className="text-muted small">
                                                                            <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                                                                            {formatLocation(event.city, event.country)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="text-end">
                                                                    {renderActionCell(event)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <ul className="list-group list-group-flush d-block d-md-none">
                                            {events.map((event) => {
                                                const failureLabel = failureLabelFor(event);
                                                return (
                                                    <li key={event.id} className="list-group-item py-3">
                                                        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {eventTypeBadge(event.event_type)}
                                                                {event.is_current_device && (
                                                                    <span className="badge badge-rounded badge-info">This device</span>
                                                                )}
                                                            </div>
                                                            <div>{renderActionCell(event)}</div>
                                                        </div>
                                                        <div className="text-muted small mb-1">
                                                            <i className="bi bi-clock me-1" aria-hidden="true" />
                                                            {formatEventDate(event.created_at)}
                                                        </div>
                                                        {failureLabel && (
                                                            <div className="text-muted small mb-1">{failureLabel}</div>
                                                        )}
                                                        <div className="text-muted small mb-1">
                                                            <i className="bi bi-laptop me-1" aria-hidden="true" />
                                                            {event.device_label || 'Unknown device'}
                                                        </div>
                                                        {event.ip_address && (
                                                            <div className="text-muted small mb-1">
                                                                <i className="bi bi-hdd-network me-1" aria-hidden="true" />
                                                                {event.ip_address}
                                                            </div>
                                                        )}
                                                        {(event.city || event.country) && (
                                                            <div className="text-muted small">
                                                                <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                                                                {formatLocation(event.city, event.country)}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </>
                                );
                            })()}
                            <PaginationBar
                                page={page}
                                lastPage={totalPages}
                                isLoading={activityQuery.isFetching}
                                onPrev={() => { setPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setPage(p => Math.min(p + 1, totalPages)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <RevokeOtherSessionsModal
                show={showRevokeModal}
                onHide={() => setShowRevokeModal(false)}
                triggeringEventUuid={pendingTriggerUuid}
            />
        </Fragment>
    );
};

export default SignInActivity;
